<template>
    <div
        class="reservation-chat-collapse"
        :class="{ 'reservation-chat-collapse--open': inlineOpen }"
        :aria-hidden="inlineOpen ? 'false' : 'true'"
        :inert="!inlineOpen"
    >
        <div class="reservation-chat-collapse-inner">
            <Teleport to="body" :disabled="!enlarged">
            <div
                class="reservation-chat-layer"
                :class="{ 'reservation-chat-layer--enlarged': enlarged }"
                @click.self="shrinkChat"
            >
            <div
                class="reservation-chat-panel border rounded-lg overflow-hidden"
                :class="enlarged ? 'overflow-hidden' : 'pa-3 mt-3'"
                @click.stop
            >
                <v-toolbar
                    v-if="enlarged"
                    color="primary"
                    density="compact"
                    class="reservation-chat-enlarged-toolbar flex-shrink-0"
                >
                    <v-row no-gutters align="center" class="flex-grow-1 px-5">
                        <v-col class="d-flex align-center min-w-0 py-0">
                            <v-toolbar-title class="text-yellow d-flex align-center">
                                <span>{{ $t("reservations.chat.title") }}</span>
                            </v-toolbar-title>
                        </v-col>
                        <v-col v-if="reservationNumber" class="d-flex align-center justify-center min-w-0 py-0">
                            <v-tooltip v-if="entrySearchUrl" location="bottom" :z-index="4000">
                                <template #activator="{ props: entryLinkTooltipProps }">
                                    <router-link
                                        v-bind="entryLinkTooltipProps"
                                        :to="entrySearchUrl"
                                        class="reservation-chat-entry-link text-h6 font-weight-regular text-truncate"
                                        @click.stop
                                    >
                                        {{ reservationNumber }}
                                    </router-link>
                                </template>
                                {{ $t("reservations.viewOriginalEntry") }}
                            </v-tooltip>
                            <span v-else class="text-yellow text-h6 font-weight-regular text-truncate">
                                {{ reservationNumber }}
                            </span>
                        </v-col>
                        <v-col class="d-flex align-center justify-end py-0">
                        <v-btn
                            v-if="canEmail"
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            color="yellow"
                            :aria-label="$t('reservations.chat.emailChat')"
                            @click="emailConfirmVisible = true"
                        >
                            <v-icon size="22">mdi-email-outline</v-icon>
                            <v-tooltip activator="parent" location="bottom" :z-index="4000">
                                {{ $t("reservations.chat.emailChat") }}
                            </v-tooltip>
                        </v-btn>
                        <v-tooltip location="bottom" :z-index="4000">
                            <template #activator="{ props: shrinkTooltipProps }">
                                <v-btn
                                    class="reservation-chat-tool-btn"
                                    icon
                                    size="x-small"
                                    variant="text"
                                    color="yellow"
                                    v-bind="shrinkTooltipProps"
                                    :aria-label="$t('reservations.chat.shrink')"
                                    @click="shrinkChat"
                                >
                                    <v-icon size="22">mdi-window-restore</v-icon>
                                </v-btn>
                            </template>
                            {{ $t("reservations.chat.shrink") }}
                        </v-tooltip>
                        <v-btn
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            color="yellow"
                            @click="closeChat"
                        >
                            <v-icon size="22">mdi-close</v-icon>
                        </v-btn>
                        </v-col>
                    </v-row>
                </v-toolbar>
                <v-tabs
                    v-if="enlarged && threads.length > 1"
                    :model-value="threadUid"
                    class="reservation-chat-fullscreen-tabs"
                    density="compact"
                    bg-color="surface"
                    color="primary"
                    slider-color="primary"
                    height="36"
                    show-arrows
                    @update:model-value="onSelectThread"
                >
                    <v-tab
                        v-for="thread in threads"
                        :key="thread.uid"
                        :value="thread.uid"
                        rounded="0"
                        elevation="0"
                        variant="text"
                        size="default"
                        class="text-none"
                    >
                        <span class="entry-chat-tab-label">
                            <span class="entry-chat-tab-side" aria-hidden="true"></span>
                            <span class="entry-chat-tab-name">{{ thread.counterpart_company?.name }}</span>
                            <span class="entry-chat-tab-side">
                                <v-slide-x-reverse-transition>
                                    <v-icon
                                        v-if="thread.reservation_uid"
                                        size="16"
                                        color="success"
                                        aria-hidden="true"
                                    >mdi-check-circle</v-icon>
                                </v-slide-x-reverse-transition>
                                <span
                                    v-if="showThreadBadge(thread)"
                                    class="entry-chat-unread-dot"
                                />
                            </span>
                        </span>
                    </v-tab>
                </v-tabs>
                <div v-if="!enlarged" class="d-flex align-center justify-space-between mb-2 ga-2">
                    <div class="d-flex align-center ga-1 flex-shrink-0">
                        <v-btn
                            v-if="showBack"
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            :aria-label="$t('entries.chat.back')"
                            @click="$emit('back')"
                        >
                            <v-icon size="22">mdi-arrow-left</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                {{ $t("entries.chat.back") }}
                            </v-tooltip>
                        </v-btn>
                        <span class="text-body-1 font-weight-bold">{{ $t("reservations.chat.title") }}</span>
                    </div>
                    <div class="d-flex align-center ga-1 flex-shrink-0">
                        <v-btn
                            v-if="canEmail"
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            :aria-label="$t('reservations.chat.emailChat')"
                            @click="emailConfirmVisible = true"
                        >
                            <v-icon size="22">mdi-email-outline</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                {{ $t("reservations.chat.emailChat") }}
                            </v-tooltip>
                        </v-btn>
                        <v-btn
                            class="reservation-chat-tool-btn"
                            icon
                            size="x-small"
                            variant="text"
                            :aria-label="$t('reservations.chat.enlarge')"
                            @click="enlargeChat"
                        >
                            <v-icon size="22">mdi-window-restore</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                {{ $t("reservations.chat.enlarge") }}
                            </v-tooltip>
                        </v-btn>
                        <v-btn class="reservation-chat-tool-btn" icon size="x-small" variant="text" @click="closeChat">
                            <v-icon size="22">mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>
                <v-window
                    :model-value="movedReservationUid ? 'moved' : 'conversation'"
                    :touch="false"
                    :show-arrows="false"
                    direction="horizontal"
                    class="reservation-chat-stage"
                    :class="enlarged ? 'flex-grow-1 min-h-0' : ''"
                >
                    <v-window-item
                        value="conversation"
                        :class="enlarged ? 'd-flex flex-column min-h-0 h-100' : ''"
                    >
                <div v-if="headerStatusNotice" class="reservation-chat-status-notice">
                    {{ headerStatusNotice }}
                </div>
                <v-tabs-window
                    v-if="threads.length > 1"
                    :model-value="stageThreadUid"
                    direction="horizontal"
                    class="reservation-chat-thread-window"
                >
                    <v-tabs-window-item
                        v-for="thread in threads"
                        :key="thread.uid"
                        :value="thread.uid"
                        :transition="holdThreadSlide ? false : undefined"
                        :reverse-transition="holdThreadSlide ? false : undefined"
                        eager
                    >
                        <div
                            :ref="(el) => setMessagesEl(thread.uid, el)"
                            class="reservation-chat-messages"
                            :class="enlarged ? 'flex-grow-1 overflow-y-auto min-h-0 px-3 pt-3' : 'mb-3'"
                        >
                            <div v-if="thread.uid === threadUid && loading && !messagesFor(thread).length" class="text-caption text-medium-emphasis px-1">
                                {{ $t("reservations.chat.loading") }}
                            </div>
                            <div v-if="noticeFor(thread)" class="reservation-chat-notice text-caption">
                                {{ noticeFor(thread) }}
                            </div>
                            <div v-else-if="!(thread.uid === threadUid && loading) && !messagesFor(thread).length" class="text-caption text-medium-emphasis px-1">
                                {{ filesOnly ? $t("reservations.chat.noFiles") : $t("reservations.chat.empty") }}
                            </div>
                            <div
                                v-for="msg in messagesFor(thread)"
                                :key="msg.uid"
                                class="reservation-chat-row"
                                :class="{ 'reservation-chat-row--mine': isMine(msg) }"
                            >
                                <div
                                    class="reservation-chat-bubble"
                                    :class="{ 'reservation-chat-bubble--mine': isMine(msg) }"
                                >
                                    <div v-if="msg.body && !filesOnly" class="reservation-chat-body">{{ msg.body }}</div>
                                    <div v-if="messageAttachments(msg).length" class="reservation-chat-files">
                                        <button
                                            v-for="(file, index) in messageAttachments(msg)"
                                            :key="`${msg.uid}-${index}`"
                                            type="button"
                                            class="reservation-chat-file"
                                            :title="file.name"
                                            @click="downloadAttachment(file)"
                                        >
                                            <v-icon size="small" class="flex-shrink-0">mdi-file-document-outline</v-icon>
                                            <span class="reservation-chat-file-name">{{ file.name }}</span>
                                        </button>
                                    </div>
                                    <div class="reservation-chat-meta">
                                        <span class="reservation-chat-meta-who">{{ senderShortLabel(msg) }}</span>
                                        <span class="reservation-chat-meta-sep" aria-hidden="true">·</span>
                                        <span>{{ formatTime(msg.created_at) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-tabs-window-item>
                </v-tabs-window>
                <div
                    v-else
                    ref="messagesEl"
                    v-auto-animate
                    class="reservation-chat-messages"
                    :class="enlarged ? 'flex-grow-1 overflow-y-auto min-h-0 px-3 pt-3' : 'mb-3'"
                >
                    <div v-if="loading" class="text-caption text-medium-emphasis px-1">
                        {{ $t("reservations.chat.loading") }}
                    </div>
                    <div v-if="noticeText" class="reservation-chat-notice text-caption">
                        {{ noticeText }}
                    </div>
                    <div v-else-if="!loading && !visibleMessages.length" class="text-caption text-medium-emphasis px-1">
                        {{ filesOnly ? $t("reservations.chat.noFiles") : $t("reservations.chat.empty") }}
                    </div>
                    <div
                        v-for="msg in visibleMessages"
                        :key="msg.uid"
                        class="reservation-chat-row"
                        :class="{ 'reservation-chat-row--mine': isMine(msg) }"
                    >
                        <div
                            v-auto-animate
                            class="reservation-chat-bubble"
                            :class="{ 'reservation-chat-bubble--mine': isMine(msg) }"
                        >
                            <div v-if="msg.body && !filesOnly" class="reservation-chat-body">{{ msg.body }}</div>
                            <div v-if="messageAttachments(msg).length" class="reservation-chat-files">
                                <button
                                    v-for="(file, index) in messageAttachments(msg)"
                                    :key="`${msg.uid}-${index}`"
                                    type="button"
                                    class="reservation-chat-file"
                                    :title="file.name"
                                    @click="downloadAttachment(file)"
                                >
                                    <v-icon size="small" class="flex-shrink-0">mdi-file-document-outline</v-icon>
                                    <span class="reservation-chat-file-name">{{ file.name }}</span>
                                </button>
                            </div>
                            <div class="reservation-chat-meta">
                                <span class="reservation-chat-meta-who">{{ senderShortLabel(msg) }}</span>
                                <span class="reservation-chat-meta-sep" aria-hidden="true">·</span>
                                <span>{{ formatTime(msg.created_at) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div :class="enlarged ? 'flex-shrink-0 bg-surface px-3 pb-3 pt-2' : ''">
                <div class="d-flex ga-2 align-start">
                    <template v-if="allowAttachments">
                        <v-btn
                            icon
                            size="small"
                            variant="text"
                            :aria-label="$t('reservations.chat.attach')"
                            @click="$refs.fileInput.click()"
                        >
                            <v-icon>mdi-paperclip</v-icon>
                            <v-tooltip activator="parent" location="top" :z-index="4000" max-width="280">
                                {{ $t("reservations.chat.attachHint") }}
                            </v-tooltip>
                        </v-btn>
                        <v-btn
                            icon
                            size="small"
                            :variant="filesOnly ? 'flat' : 'text'"
                            :color="filesOnly ? 'primary' : undefined"
                            :aria-pressed="filesOnly ? 'true' : 'false'"
                            :aria-label="$t('reservations.chat.showOnlyMedia')"
                            @click="filesOnly = !filesOnly"
                        >
                            <v-icon :class="{ 'text-yellow': filesOnly }">mdi-file-document-outline</v-icon>
                            <v-tooltip activator="parent" location="top" :z-index="4000">
                                {{ $t("reservations.chat.showOnlyMedia") }}
                            </v-tooltip>
                        </v-btn>
                        <input
                            ref="fileInput"
                            type="file"
                            class="d-none"
                            multiple
                            accept=".pdf,.doc,.jpg,.jpeg,.png,application/pdf,application/msword,image/jpeg,image/png"
                            @change="onFilePicked"
                        />
                    </template>
                    <div class="flex-grow-1">
                        <v-textarea
                            ref="draftField"
                            v-model="draft"
                            :placeholder="$t('reservations.chat.placeholder')"
                            :maxlength="chatMax"
                            rows="1"
                            max-rows="3"
                            auto-grow
                            hide-details
                            density="compact"
                            variant="outlined"
                            @keydown.enter.exact.prevent="send"
                        />
                        <div class="reservation-chat-counter text-caption text-medium-emphasis text-start mt-1">
                            {{ draft.length }}/{{ chatMax }}
                        </div>
                    </div>
                    <v-btn
                        class="mt-1"
                        color="primary"
                        size="small"
                        :loading="sending"
                        :disabled="!canSend"
                        @click="send"
                    >
                        {{ $t("reservations.chat.send") }}
                    </v-btn>
                </div>
                <div v-if="allowAttachments && selectedFiles.length" class="reservation-chat-chips mt-2">
                    <div
                        v-for="(file, index) in selectedFiles"
                        :key="`${file.name}-${index}`"
                        class="reservation-chat-chip"
                    >
                        <v-icon size="small" class="flex-shrink-0">mdi-file-document-outline</v-icon>
                        <span class="reservation-chat-chip-name" :title="file.name">{{ file.name }}</span>
                        <button
                            type="button"
                            class="reservation-chat-chip-remove"
                            :aria-label="$t('reservations.chat.removeFile')"
                            @click="removeFile(index)"
                        >
                            <v-icon size="14">mdi-close</v-icon>
                        </button>
                    </div>
                </div>
                <div v-if="error" class="text-caption text-error mt-1">{{ error }}</div>
                <div v-else-if="emailStatus" class="text-caption text-medium-emphasis mt-2">{{ emailStatus }}</div>
                </div>
                    </v-window-item>
                    <v-window-item value="moved">
                        <div
                            class="d-flex flex-column align-center justify-center text-center"
                            :class="enlarged ? 'h-100 px-6' : 'py-8 px-4'"
                        >
                            <v-btn color="success" variant="flat" @click="goToReservationChat">
                                {{ $t("reservations.chat.goToReservation") }}
                            </v-btn>
                        </div>
                    </v-window-item>
                </v-window>
            </div>
            </div>
            </Teleport>
        </div>
    </div>
    <ConfirmDialog
        :visible="emailConfirmVisible"
        :title="$t('reservations.chat.emailConfirmTitle')"
        :message="$t('reservations.chat.emailConfirm', { email: emailAddress })"
        :primary-text="$t('common.yes')"
        :secondary-text="$t('common.no')"
        secondary-color="error"
        @update:visible="(open) => { emailConfirmVisible = open; }"
        @primary="confirmEmailTranscript"
        @secondary="emailConfirmVisible = false"
    />
</template>

<script>
import api from "@/api";
import emitter from "@/eventBus";
import { formatLocalTime } from "@/utils";
import ConfirmDialog from "@/components/display/ConfirmDialog.vue";

const CHAT_MAX = 500;
const FILE_MAX = 5 * 1024 * 1024;
const FILE_MAX_COUNT = 2;
const FILE_EXT = new Set(["pdf", "doc", "jpg", "jpeg", "png"]);

export default {
    name: "ReservationChatPanel",
    components: { ConfirmDialog },
    props: {
        reservationUid: { type: String, default: "" },
        unreadCount: { type: Number, default: 0 },
        expanded: { type: Boolean, default: false },
        reservationNumber: { type: String, default: "" },
        entrySearchUrl: { type: Object, default: null },
        entryType: { type: String, default: "" },
        entryUid: { type: String, default: "" },
        threadUid: { type: String, default: "" },
        noticeCode: { type: String, default: "" },
        viewerIsOwner: { type: Boolean, default: false },
        viewerEmail: { type: String, default: "" },
        threads: { type: Array, default: () => [] },
        showBack: { type: Boolean, default: false },
        allowEmail: { type: Boolean, default: false },
        allowAttachments: { type: Boolean, default: true },
        badgeHoldUid: { type: String, default: "" },
    },
    emits: ["read", "sent", "update:expanded", "back", "linked", "moved", "removed", "notice", "update:threadUid"],
    data() {
        return {
            chatMax: CHAT_MAX,
            loading: false,
            sending: false,
            sendingEmail: false,
            emailConfirmVisible: false,
            emailStatus: "",
            messages: [],
            draft: "",
            selectedFiles: [],
            enlarged: false,
            filesOnly: false,
            inlineOpen: false,
            layoutToken: 0,
            error: null,
            socket: null,
            panelEpoch: 0,
            sendEpoch: 0,
            loadingEpoch: 0,
            panes: {},
            pinnedThreadUid: "",
            holdThreadSlide: false,
            activeMessagesEl: null,
            currentUserId: null,
            sessionEmail: "",
            serverNotice: "",
            liveNotice: null,
        };
    },
    computed: {
        canEmail() {
            return this.allowEmail && Boolean(this.reservationUid);
        },
        emailAddress() {
            return this.viewerEmail || this.sessionEmail || "";
        },
        activeNoticeCode() {
            if (this.liveNotice !== null) return this.liveNotice;
            return this.serverNotice || this.noticeCode || "";
        },
        headerStatusNotice() {
            const code = this.activeNoticeCode;
            if (code === "pending" || (code === "pending_reserver" && this.viewerIsOwner)) {
                return this.$t("reservations.chat.noticePending");
            }
            if (code === "pending_reserver") return this.$t("reservations.chat.noticePendingReserver");
            if (code === "active_reserver") return this.$t("reservations.chat.noticeActiveReserver");
            if (code === "active_other") return this.$t("reservations.chat.noticeActiveOther");
            return "";
        },
        noticeText() {
            return "";
        },
        canSend() {
            const text = this.draft.trim();
            const hasFile = this.selectedFiles.length > 0;
            if (this.sending) return false;
            if (!text && !hasFile) return false;
            return text.length <= CHAT_MAX;
        },
        visibleMessages() {
            if (!this.filesOnly) return this.messages;
            return this.messages.filter((msg) => this.messageAttachments(msg).length > 0);
        },
        movedReservationUid() {
            const thread = this.threads.find((item) => item.uid === this.threadUid);
            return thread?.reservation_uid || "";
        },
        stageThreadUid() {
            return this.pinnedThreadUid || this.threadUid;
        },
    },
    watch: {
        expanded(open) {
            if (open) {
                this.inlineOpen = true;
                this.openPanel();
            } else {
                this.panelEpoch += 1;
                this.layoutToken += 1;
                this.inlineOpen = false;
                this.enlarged = false;
                this.closeSocket();
            }
        },
        enlarged(open) {
            document.body.style.overflow = open ? "hidden" : "";
            if (open) this.$nextTick(() => this.scrollToBottom());
        },
        reservationUid(next, prev) {
            if (this.threads.length > 1) return;
            if (!next && !prev) return;
            this.messages = [];
            this.serverNotice = "";
            this.filesOnly = false;
            this.layoutToken += 1;
            this.enlarged = false;
            this.inlineOpen = this.expanded;
            if (this.expanded) {
                this.openPanel();
            }
        },
        threadUid(next, prev) {
            const threadMoved = (uid) => !!this.threads.find((item) => item.uid === uid)?.reservation_uid;
            const nextMoved = threadMoved(next);
            const prevMoved = threadMoved(prev);
            if (next && !nextMoved) {
                if (!prevMoved) this.holdThreadSlide = false;
                this.pinnedThreadUid = next;
            } else if (nextMoved && prev && !prevMoved) {
                this.pinnedThreadUid = prev;
            }
            if (!nextMoved && prevMoved) this.holdThreadSlide = true;
            this.liveNotice = null;
            if (!this.expanded) return;
            if (this.threads.length > 1) {
                this.rememberPane(prev);
                const cached = next ? this.panes[next] : null;
                this.messages = cached ? cached.messages.slice() : [];
                this.serverNotice = cached?.notice || "";
                this.filesOnly = false;
                this.openPanel();
                return;
            }
            if (this.reservationUid) return;
            this.messages = [];
            this.serverNotice = "";
            this.openPanel();
        },
        noticeCode(code) {
            if (!this.serverNotice) return;
            if (code) this.serverNotice = code;
        },
        movedReservationUid(uid) {
            if (uid) this.closeSocket();
        },
    },
    created() {
        try {
            const user = JSON.parse(sessionStorage.getItem("user") || "{}");
            this.currentUserId = user?.id ?? null;
            this.sessionEmail = user?.email || "";
        } catch {
            this.currentUserId = null;
            this.sessionEmail = "";
        }
    },
    mounted() {
        window.addEventListener("keydown", this.onEscape);
        if (this.expanded) this.openAfterPaint();
    },
    beforeUnmount() {
        this.panelEpoch += 1;
        document.body.style.overflow = "";
        window.removeEventListener("keydown", this.onEscape);
        this.closeSocket();
    },
    methods: {
        openAfterPaint() {
            this.inlineOpen = false;
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.inlineOpen = true;
                    this.openPanel();
                });
            });
        },
        wait(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        collapseDuration() {
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0;
            return 300;
        },
        async enlargeChat() {
            if (this.enlarged) return;
            const token = ++this.layoutToken;
            this.inlineOpen = false;
            await this.wait(this.collapseDuration());
            if (token !== this.layoutToken || !this.expanded) return;
            this.enlarged = true;
        },
        async shrinkChat() {
            if (!this.enlarged) return;
            const token = ++this.layoutToken;
            this.enlarged = false;
            await this.$nextTick();
            await this.wait(this.collapseDuration() ? 40 : 0);
            if (token !== this.layoutToken || !this.expanded) return;
            this.inlineOpen = true;
        },
        closeChat() {
            this.layoutToken += 1;
            this.inlineOpen = false;
            this.enlarged = false;
            this.$emit("update:expanded", false);
        },
        onEscape(event) {
            if (event.key === "Escape" && this.enlarged) {
                this.shrinkChat();
            }
        },
        isMine(msg) {
            return msg?.sender?.id != null && msg.sender.id === this.currentUserId;
        },
        senderShortLabel(msg) {
            if (this.isMine(msg)) {
                return this.$t("reservations.chat.you");
            }
            const first = (msg.sender?.first_name || "").trim();
            const company = (msg.sender_company?.name || "").trim();
            if (first && company) return `${first} · ${company}`;
            return first || company || "—";
        },
        formatTime(iso) {
            if (!iso) return "";
            return formatLocalTime(iso, "HH:mm, DD.MM.YY");
        },
        focusDraft() {
            this.$nextTick(() => {
                const field = this.$refs.draftField;
                if (!field) return;
                const textarea =
                    field.$el?.querySelector?.("textarea") ||
                    (field.tagName === "TEXTAREA" ? field : null);
                if (textarea && typeof textarea.focus === "function") {
                    try {
                        textarea.focus({ preventScroll: true });
                    } catch {
                        textarea.focus();
                    }
                    return;
                }
                if (typeof field.focus === "function") {
                    field.focus();
                }
            });
        },
        messagesUrl() {
            if (this.reservationUid) {
                return `/reservations/${this.reservationUid}/chat/messages/`;
            }
            if (this.threadUid) {
                return `/entries/${this.entryType}/${this.entryUid}/chat/threads/${this.threadUid}/messages/`;
            }
            if (this.entryUid) {
                return `/entries/${this.entryType}/${this.entryUid}/chat/messages/`;
            }
            return "";
        },
        readUrl() {
            if (this.reservationUid) {
                return `/reservations/${this.reservationUid}/chat/read/`;
            }
            if (this.threadUid) {
                return `/entries/${this.entryType}/${this.entryUid}/chat/threads/${this.threadUid}/read/`;
            }
            return "";
        },
        emailUrl() {
            if (this.reservationUid) {
                return `/reservations/${this.reservationUid}/chat/email/`;
            }
            if (this.threadUid) {
                return `/entries/${this.entryType}/${this.entryUid}/chat/threads/${this.threadUid}/email/`;
            }
            return "";
        },
        isCurrentPanel(epoch, threadUid, reservationUid) {
            return epoch === this.panelEpoch
                && threadUid === this.threadUid
                && reservationUid === this.reservationUid;
        },
        threadMovedAway(threadUid) {
            if (this.reservationUid) return false;
            const thread = this.threads.find((item) => item.uid === threadUid);
            return Boolean(thread?.reservation_uid);
        },
        async openPanel() {
            const epoch = ++this.panelEpoch;
            const threadUid = this.threadUid;
            const reservationUid = this.reservationUid;
            if (this.threadMovedAway(threadUid)) {
                this.closeSocket();
                return;
            }
            const url = this.messagesUrl();
            const readUrl = this.readUrl();
            if (!url) return;
            await this.fetchMessages({ epoch, url, threadUid, reservationUid });
            if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
            if (this.threadMovedAway(threadUid)) {
                this.closeSocket();
                return;
            }
            await this.markRead(readUrl);
            if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
            if (this.threadMovedAway(threadUid)) {
                this.closeSocket();
                return;
            }
            this.connectSocket({ epoch, threadUid, reservationUid });
            this.$nextTick(() => {
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.scrollToBottom();
                this.focusDraft();
            });
        },
        applyThreadMeta(data, requestedThreadUid = this.threadUid) {
            if (requestedThreadUid !== this.threadUid) return false;
            if (data?.notice) this.serverNotice = data.notice;
            else if (data && Object.prototype.hasOwnProperty.call(data, "notice")) {
                this.serverNotice = data.notice || "";
            }
            const threadUid = data?.thread_uid || data?.entry_thread_uid;
            if (threadUid && threadUid !== this.threadUid) {
                this.$emit("update:threadUid", threadUid);
            }
            if (data?.reservation_uid && !this.reservationUid && this.entryUid) {
                if (this.viewerIsOwner) {
                    this.$emit("moved", {
                        threadUid: this.threadUid || data.thread_uid,
                        reservationUid: data.reservation_uid,
                    });
                } else {
                    this.$emit("linked", { reservationUid: data.reservation_uid });
                }
                return true;
            }
            return false;
        },
        async fetchMessages(attempt) {
            const epoch = attempt?.epoch ?? this.panelEpoch;
            const threadUid = attempt?.threadUid ?? this.threadUid;
            const reservationUid = attempt?.reservationUid ?? this.reservationUid;
            const url = attempt?.url || this.messagesUrl();
            if (!url) return;
            if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
            const showLoading = this.messages.length === 0;
            if (showLoading) {
                this.loading = true;
                this.loadingEpoch = epoch;
            }
            this.error = null;
            try {
                const { data } = await api.get(url, { params: { limit: 50 } });
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                if (this.applyThreadMeta(data, threadUid)) return;
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.messages = data.results || [];
                this.rememberPane(this.threadUid);
            } catch (e) {
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.error = e.response?.data?.error || this.$t("reservations.chat.loadError");
            } finally {
                if (this.loadingEpoch === epoch) this.loading = false;
            }
        },
        async markRead(url = this.readUrl()) {
            if (!url) return;
            const epoch = this.panelEpoch;
            const threadUid = this.threadUid;
            const reservationUid = this.reservationUid;
            try {
                await api.post(url);
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.$emit("read", reservationUid || threadUid);
                emitter.emit("refresh-chat-unread");
            } catch {
                /* ignore */
            }
        },
        async confirmEmailTranscript() {
            const url = this.emailUrl();
            if (!url || this.sendingEmail) return;
            this.sendingEmail = true;
            this.error = null;
            this.emailStatus = "";
            try {
                await api.post(url);
                this.emailConfirmVisible = false;
                this.emailStatus = this.$t("reservations.chat.emailSent", {
                    when: formatLocalTime(new Date().toISOString(), "HH:mm, DD.MM.YY"),
                });
            } catch (e) {
                this.emailConfirmVisible = false;
                this.error = e.response?.data?.error || this.$t("reservations.chat.emailError");
            } finally {
                this.sendingEmail = false;
            }
        },
        messageAttachments(msg) {
            if (Array.isArray(msg?.attachments)) return msg.attachments;
            return msg?.attachment ? [msg.attachment] : [];
        },
        onFilePicked(event) {
            const picked = Array.from(event.target.files || []);
            event.target.value = "";
            if (!picked.length) return;
            const next = this.selectedFiles.slice();
            let error = null;
            for (const file of picked) {
                const ext = (file.name.split(".").pop() || "").toLowerCase();
                if (!FILE_EXT.has(ext)) {
                    error = this.$t("reservations.chat.fileType");
                    continue;
                }
                if (file.size > FILE_MAX) {
                    error = this.$t("reservations.chat.fileTooLarge");
                    continue;
                }
                if (next.length >= FILE_MAX_COUNT) {
                    error = this.$t("reservations.chat.fileLimit");
                    break;
                }
                next.push(file);
            }
            this.selectedFiles = next;
            this.error = error;
        },
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
        },
        async downloadAttachment(file) {
            if (!file?.url) return;
            try {
                const response = await api.get(file.url, { responseType: "blob" });
                const objectUrl = URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = file.name || "download";
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(objectUrl);
            } catch (e) {
                this.error = e.response?.data?.error || this.$t("reservations.chat.sendError");
            }
        },
        async send() {
            const url = this.messagesUrl();
            if (!this.canSend || !url) return;
            const epoch = this.panelEpoch;
            const threadUid = this.threadUid;
            const reservationUid = this.reservationUid;
            const sendEpoch = ++this.sendEpoch;
            const body = this.draft.trim();
            const files = this.selectedFiles.slice();
            this.sending = true;
            this.error = null;
            try {
                let data;
                if (files.length) {
                    const form = new FormData();
                    form.append("body", body);
                    files.forEach((file) => form.append("file", file));
                    ({ data } = await api.post(url, form));
                } else {
                    ({ data } = await api.post(url, { body }));
                }
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.draft = "";
                this.selectedFiles = [];
                if (this.applyThreadMeta(data, threadUid)) return;
                if (!this.messages.some((m) => m.uid === data.uid)) {
                    this.messages.push(data);
                    this.rememberPane(this.threadUid);
                }
                this.$emit("sent", data);
                this.$nextTick(() => {
                    if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                    this.scrollToBottom();
                    this.focusDraft();
                });
            } catch (e) {
                if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
                this.error = e.response?.data?.error || this.$t("reservations.chat.sendError");
            } finally {
                if (sendEpoch === this.sendEpoch) {
                    this.sending = false;
                    if (this.isCurrentPanel(epoch, threadUid, reservationUid)) this.focusDraft();
                }
            }
        },
        appendMessage(msg) {
            if (!msg?.uid) return;
            if (this.messages.some((m) => m.uid === msg.uid)) return;
            this.messages.push(msg);
            this.rememberPane(this.threadUid);
            this.$nextTick(() => this.scrollToBottom());
            if (this.expanded && !this.isMine(msg)) {
                this.markRead();
            }
        },
        async connectSocket(attempt) {
            const epoch = attempt?.epoch ?? this.panelEpoch;
            const threadUid = attempt?.threadUid ?? this.threadUid;
            const reservationUid = attempt?.reservationUid ?? this.reservationUid;
            if (!this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
            if (this.threadMovedAway(threadUid)) {
                this.closeSocket();
                return;
            }
            this.closeSocket();
            let ticketUrl = "";
            let wsPath = "";
            if (reservationUid) {
                ticketUrl = `/reservations/${reservationUid}/chat/ws-ticket/`;
                wsPath = `/ws/chat/${reservationUid}/`;
            } else if (threadUid) {
                ticketUrl = `/entries/${this.entryType}/${this.entryUid}/chat/threads/${threadUid}/ws-ticket/`;
                wsPath = `/ws/entry-chat/${threadUid}/`;
            } else {
                return;
            }
            let ticket;
            try {
                const { data } = await api.post(ticketUrl);
                ticket = data?.ticket;
            } catch {
                return;
            }
            if (!ticket || !this.isCurrentPanel(epoch, threadUid, reservationUid)) return;
            if (this.threadMovedAway(threadUid)) return;
            const base = process.env.VUE_APP_API_URL.replace(/^(https?):\/\//, (_, proto) =>
                proto === "https" ? "wss://" : "ws://"
            ).replace(/\/api\/?$/, "");
            const wsUrl = `${base}${wsPath}?ticket=${encodeURIComponent(ticket)}`;
            try {
                const socket = new WebSocket(wsUrl);
                this.socket = socket;
                socket.onmessage = (event) => {
                    if (this.socket !== socket) return;
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === "chat_message" && data.message) {
                            this.appendMessage(data.message);
                        } else if (data.type === "entry_chat_linked" && data.reservation_uid) {
                            if (this.viewerIsOwner) {
                                this.$emit("moved", {
                                    threadUid: this.threadUid,
                                    reservationUid: data.reservation_uid,
                                });
                            } else {
                                this.$emit("linked", { reservationUid: data.reservation_uid });
                            }
                        } else if (data.type === "chat_refetch") {
                            this.fetchMessages();
                        } else if (data.type === "entry_chat_notice") {
                            this.applyLiveNotice(data.notice || "");
                        } else if (data.type === "entry_chat_removed") {
                            this.$emit("removed");
                        }
                    } catch {
                        /* ignore */
                    }
                };
            } catch {
                /* ignore */
            }
        },
        closeSocket() {
            const socket = this.socket;
            if (!socket) return;
            this.socket = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.onclose = null;
            if (socket.readyState === WebSocket.CONNECTING) {
                socket.onopen = () => {
                    socket.onmessage = null;
                    try {
                        socket.close();
                    } catch {
                        /* ignore */
                    }
                };
                return;
            }
            if (socket.readyState === WebSocket.CLOSED) return;
            try {
                socket.close();
            } catch {
                /* ignore */
            }
        },
        rememberPane(uid) {
            if (!uid) return;
            this.panes[uid] = {
                messages: this.messages.slice(),
                notice: this.serverNotice,
            };
        },
        messagesFor(thread) {
            const list = thread.uid === this.threadUid
                ? this.messages
                : (this.panes[thread.uid]?.messages || []);
            if (!this.filesOnly) return list;
            return list.filter((msg) => this.messageAttachments(msg).length > 0);
        },
        applyLiveNotice(notice) {
            const code = notice || "";
            this.liveNotice = code;
            this.serverNotice = code;
            if (!this.threadUid) return;
            if (this.panes[this.threadUid]) this.panes[this.threadUid].notice = code;
            else this.panes[this.threadUid] = { messages: this.messages.slice(), notice: code };
            this.$emit("notice", { threadUid: this.threadUid, notice: code });
        },
        noticeFor() {
            return "";
        },
        setMessagesEl(uid, el) {
            if (uid === this.threadUid) this.activeMessagesEl = el;
        },
        showThreadBadge(thread) {
            if (!thread || thread.reservation_uid) return false;
            if (thread.uid === this.badgeHoldUid) return true;
            return thread.unread_count > 0 && thread.uid !== this.threadUid;
        },
        goToReservationChat() {
            const reservationUid = this.movedReservationUid;
            if (!reservationUid) return;
            const name = this.entryType === "ware" ? "WareReservations" : "TruckReservations";
            this.$router.push({
                name,
                query: { reservation: reservationUid, chat: "1" },
            });
        },
        onSelectThread(uid) {
            if (!uid || uid === this.threadUid) return;
            this.$emit("update:threadUid", uid);
        },
        scrollToBottom() {
            const el = this.activeMessagesEl || this.$refs.messagesEl;
            const node = Array.isArray(el) ? el.find(Boolean) : el;
            if (node) node.scrollTop = node.scrollHeight;
        },
    },
};
</script>

<style scoped>
.reservation-chat-entry-link {
    color: #ffeb3b;
    text-decoration: none;
}
.reservation-chat-entry-link:hover {
    color: inherit;
    text-decoration: none;
}
.reservation-chat-collapse {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.28s ease;
}
.reservation-chat-collapse--open {
    grid-template-rows: 1fr;
}
.reservation-chat-collapse-inner {
    overflow: hidden;
    min-height: 0;
}
.reservation-chat-tool-btn {
    width: 28px;
    height: 28px;
}
.reservation-chat-layer--enlarged {
    position: fixed;
    inset: 0;
    z-index: 2500;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 24px;
    background: rgba(15, 23, 42, 0.48);
    animation: reservation-chat-layer-in 0.18s ease;
}
@keyframes reservation-chat-layer-in {
    from { opacity: 0; }
    to { opacity: 1; }
}
.reservation-chat-layer--enlarged .reservation-chat-panel {
    width: min(880px, 100%);
    height: min(78vh, 720px);
    max-height: calc(100vh - 64px);
    margin: 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
    opacity: 1;
    transform: none;
}
.reservation-chat-layer--enlarged .reservation-chat-messages {
    flex: 1 1 auto;
    max-height: none;
    min-height: 0;
}
.reservation-chat-fullscreen-tabs {
    flex: 0 0 auto;
}
.reservation-chat-counter {
    line-height: 1.2;
}
.reservation-chat-fullscreen-tabs :deep(.v-tab),
.reservation-chat-fullscreen-tabs :deep(.v-btn__content) {
    justify-content: center;
}
.reservation-chat-fullscreen-tabs :deep(.v-btn__content) {
    width: 100%;
}
.entry-chat-tab-label {
    display: grid;
    grid-template-columns: 1rem auto 1rem;
    align-items: center;
    column-gap: 8px;
    width: 100%;
}
.entry-chat-tab-name {
    text-align: center;
}
.entry-chat-tab-side {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1rem;
}
.entry-chat-unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgb(var(--v-theme-error));
    flex-shrink: 0;
}
.reservation-chat-thread-window {
    min-height: 0;
}
.reservation-chat-layer--enlarged .reservation-chat-thread-window {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.reservation-chat-layer--enlarged :deep(.v-window__container) {
    height: 100% !important;
    transition: none;
}
.reservation-chat-layer--enlarged :deep(.v-window-item) {
    height: 100%;
    min-height: 0;
}
.reservation-chat-layer--enlarged :deep(.v-window-item) .reservation-chat-messages {
    height: 100%;
}
.reservation-chat-panel {
    width: 100%;
    background: rgba(var(--v-theme-surface), 1);
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.22s ease, transform 0.28s ease;
}
.reservation-chat-collapse--open .reservation-chat-panel {
    opacity: 1;
    transform: translateY(0);
}
.reservation-chat-messages {
    max-height: 180px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.reservation-chat-status-notice {
    flex: 0 0 auto;
    margin-bottom: 8px;
    padding: 10px 14px;
    border-radius: 8px;
    background: rgba(var(--v-theme-primary), 0.1);
    text-align: center;
    font-size: 1rem;
    line-height: 1.45;
}
.reservation-chat-layer--enlarged .reservation-chat-status-notice {
    margin: 12px 16px 0;
}
.reservation-chat-notice {
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(var(--v-theme-primary), 0.08);
    line-height: 1.45;
}
.reservation-chat-row {
    display: flex;
    justify-content: flex-start;
    min-width: 0;
    max-width: 100%;
}
.reservation-chat-row--mine {
    justify-content: flex-end;
}
.reservation-chat-bubble {
    max-width: 100%;
    min-width: 0;
    padding: 8px 12px 6px;
    border-radius: 14px 14px 14px 4px;
    background: rgba(var(--v-theme-on-surface), 0.06);
}
.reservation-chat-bubble--mine {
    border-radius: 14px 14px 4px 14px;
    background: rgba(var(--v-theme-primary), 0.16);
}
.reservation-chat-body {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 0.925rem;
    line-height: 1.35;
}
.reservation-chat-files {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;
    min-width: 0;
    max-width: 100%;
}
.reservation-chat-file {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-align: left;
}
.reservation-chat-file-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: underline;
}
.reservation-chat-chips {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.reservation-chat-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1 1 0;
    max-width: max-content;
    min-width: 0;
    overflow: hidden;
    padding: 2px 2px 2px 8px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.08);
}
.reservation-chat-chip-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8rem;
}
.reservation-chat-chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    margin-left: 2px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;
}
.reservation-chat-chip-remove:hover {
    background: rgba(var(--v-theme-on-surface), 0.12);
}
.reservation-chat-meta {
    margin-top: 4px;
    font-size: 0.7rem;
    line-height: 1.2;
    opacity: 0.65;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
}
.reservation-chat-row--mine .reservation-chat-meta {
    justify-content: flex-end;
}
.reservation-chat-meta-who {
    max-width: 14rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
    .reservation-chat-collapse,
    .reservation-chat-panel,
    .reservation-chat-layer--enlarged {
        transition: none;
        animation: none;
    }
}
</style>
